import React, { useMemo, useState } from 'react';
import { isUndefined } from 'lodash/fp';
import classnames from 'classnames';

import { OverflowTooltip } from '../tooltip';
import styles from './Table.module.scss';
import { ArkTableDocumentEntity, PrincipalEntity } from '../../documents/store/types';
import { Icon } from '../icon/Icon';
import { Eye, Home, User, Agency } from '../icons';
import { Position, PositionModal } from '../modal/PositionModal';

export interface PrincipleEntitiesModalProps {
    isOpen: boolean;
    closeModal: () => void;
    position: Position | null;
    principalEntities: PrincipalEntity[];
    entityName: string;
    entityOnWatchlist: number | null;
    myCompanyEntity: number | null;
}

const MAX_HEIGHT = 200;
const AGENT_ENTITY = 58;
const PRINCIPAL_LABEL = 27;
const PRINCIPAL_HEIGHT = 22;

export const PrincipleEntitiesModal: React.FC<PrincipleEntitiesModalProps> = ({ isOpen, principalEntities, closeModal, position, entityName, entityOnWatchlist, myCompanyEntity }) => {

    const height = (AGENT_ENTITY + PRINCIPAL_LABEL) + (PRINCIPAL_HEIGHT * principalEntities.length);
    const modalHeight = height < MAX_HEIGHT ? height : MAX_HEIGHT;

    const showIcon = myCompanyEntity === 1 || entityOnWatchlist === 1;
    const icon = entityOnWatchlist === 1 ? Eye : Home;

    return (
        <PositionModal
            isOpen={isOpen}
            closeModal={closeModal}
            position={position}
            testId='entity-cell-agency-entity'
            width='230px'
            height={`${modalHeight}px`}
        >
            <div className={styles.principalEntitiesWrapper}>
                <div className={styles.agentEntityWrapper}>
                    <div className={styles.agentEntityLabel}>Entity acting as agent:</div>
                    <div className={styles.agent}>
                        <OverflowTooltip className={styles.agentEntity} overlayText={entityName} testId='entity-cell-agency-entity-modal-agent-entity' />
                        {showIcon && <Icon icon={icon} fontSize={20} />}
                    </div>
                </div>
                <div className={styles.principalEntitiesLabel}>Principals in agreement</div>
                {principalEntities!.map(({ name, onWatchlist }, index) => (
                    <div key={index} className={styles.principalEntityWrapper}>
                        <OverflowTooltip className={styles.principalEntity} overlayText={`- ${name}`} testId={`entity-cell-agency-entity-modal-principal-entity-${index}`} />
                        {onWatchlist === 1 && <Icon icon={Eye} fontSize={20} />}
                    </div>
                ))}
            </div>
        </PositionModal>
    );
};

export const EntityCell: React.FC<{ value: ArkTableDocumentEntity; testId?: string; }> = ({ value, testId }) => {

    const { entityName, entityOnWatchlist, myCompanyEntity, isAgentEntity, principalEntities } = value;
    const showIcon = entityOnWatchlist === 1 || myCompanyEntity === 1 || !!isAgentEntity;

    const icon = useMemo(() => {
        if (isAgentEntity) {
            return Agency;
        }
        if (myCompanyEntity === 1) {
            return Home;
        }
        if (entityOnWatchlist === 1) {
            return Eye;
        }
        return User;
    }, [myCompanyEntity, entityOnWatchlist, isAgentEntity]);

    const [agencyModalOpen, setAgencyModalOpen] = useState<boolean>(false);
    const [position, setPosition] = useState<Position | null>(null);

    const showPrincipalEntities = useMemo(() => isAgentEntity && !isUndefined(principalEntities) && principalEntities?.length > 0, [isAgentEntity, principalEntities]);

    const openModal = (x: number, y: number) => {
        if (showPrincipalEntities) {
            setAgencyModalOpen(true);
            setPosition({ x, y });
        }
    };

    const closeModal = () => {
        setAgencyModalOpen(false);
        setPosition(null);
    };

    if (isUndefined(value)) {
        return null;
    }

    return (
        <>
            <div className={styles.entityCellWrapper} onClick={e => openModal(e.clientX, e.clientY)}>
                <OverflowTooltip className={classnames(styles.entityValue, { [styles.entityValueWithIcon]: showIcon })} overlayText={entityName} testId={testId} />
                {showIcon && <Icon icon={icon} fontSize={20} />}
            </div>
            {agencyModalOpen && <PrincipleEntitiesModal isOpen={agencyModalOpen} entityName={entityName!} entityOnWatchlist={entityOnWatchlist} myCompanyEntity={myCompanyEntity} position={position} closeModal={closeModal} principalEntities={principalEntities!} />}
        </>
    );
};
